import { RequestHeader } from "@features/cx/shared/components/RequestHeader";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import { CheckCircle, XCircle, Pause, Play } from "lucide-react";
import { toast } from "@shared/ui/use-toast";
import React, { useState, useEffect } from "react";
import {
  useAcknowledgeBulkRequest,
  useApproveBulkRequest,
  useBulkDisconnectDetails,
  useHoldBulkRequest,
  useRejectBulkRequest,
  useResumeBulkRequest,
  useUpdateDisconnectionRequest,
  useBulkRequestConsumersDownload,
  useDownloadAllBulkRequestBills,
  useBulkRequestConsumersList,
} from "@features/cx/disconnect/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "@features/cx/shared/utils/constants";

import { QuickActions } from "../quickActions/quickActions";
import { BulkDisconnectDetailCard } from "../detailCard/detailCard";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { CommunicationDialog } from "@features/cx/shared/components/CommunicationDialog";
import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import {
  useChannelTemplates,
  useChannelType,
  useSendCommunication,
} from "@features/cx/consumerManagement/hooks";
import { Button } from "@shared/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageHeader } from "@shared/components/PageHeader";
import ConsumersBulkDisconnect from "./tabs/consumers/consumers";
import MeterReadingsBulkDisconnect from "./tabs/meterReadings/meterReadings";
import BillGenerationBulkDisconnect from "./tabs/billGeneration/billGeneration";
import SharedDetailsBulkDisconnect from "./tabs/sharedDetails/sharedDetails";
import { Tabs } from "@shared/ui/tabs";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { TIMELINE_LIMIT } from "@shared/utils/constants";
import { queryClient } from "@shared/api/queries/queryClient";

const BulkDisconnectLanding = () => {
  const [currentStatus, setCurrentStatus] = useState<
    | "pending"
    | "in progress"
    | "completed"
    | "cancelled"
    | "draft"
    | "submitted"
    | "approved"
    | "rejected"
    | "on hold"
  >("pending");
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: requestDetails } = useBulkDisconnectDetails(id || "");
  const { mutateAsync: acknowledgeRequest } = useAcknowledgeBulkRequest(
    id || "",
  );
  const { mutateAsync: approveRequest } = useApproveBulkRequest(id || "");
  const { mutateAsync: rejectRequest } = useRejectBulkRequest(id || "");
  const { mutateAsync: holdRequest } = useHoldBulkRequest(id || "");
  const { mutateAsync: resumeRequest } = useResumeBulkRequest(id || "");
  const { download: downloadConsumers, isDownloading: isExporting } =
    useBulkRequestConsumersDownload(id || "");
  const { download: downloadBills, isDownloading: isDownloadingBills } =
    useDownloadAllBulkRequestBills(id || "");

  const { data: consumersListResponse } = useBulkRequestConsumersList({
    batchId: id || "",
    remoteUtilityId: Number(remoteUtilityId),
    page: 1,
    limit: 100, // Fetch enough to cover common bulk sizes
  });

  const emails =
    consumersListResponse?.results
      ?.map((c: any) => c.email)
      .filter(Boolean)
      .join(",") || "";
  const phones =
    consumersListResponse?.results
      ?.map((c: any) => c.contactNo)
      .filter(Boolean)
      .join(",") || "";

  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isNotifyDialogOpen, setNotifyDialogOpen] = useState(false);

  // Update currentStatus when API data is loaded
  useEffect(() => {
    if (requestDetails?.statusDisplay) {
      const statusFromAPI = requestDetails.statusDisplay.toLowerCase();
      setCurrentStatus(statusFromAPI as any);
    }
  }, [requestDetails]);

  // Communication Handlers
  const { mutateAsync: sendCommunication } = useSendCommunication(id || "");
  const createCommunicationWrapper = (consumerId: string) => ({
    mutateAsync: (data: any) => sendCommunication(data),
  });

  const handleAcknowledge = async () => {
    await acknowledgeRequest();
    setCurrentStatus("in progress");
    toast({
      title: "Success",
      description: "Bulk request acknowledged and moved to In Progress!",
      variant: "default",
    });
    queryClient.refetchQueries({
      queryKey: ["cx", "disconnectionRequests", "summary"],
    });
    queryClient.refetchQueries({
      queryKey: ["cx", "disconnectionRequests", "list"],
    });
  };

  const handlePutOnHold = async () => {
    await holdRequest();
    setCurrentStatus("on hold");
    toast({
      title: "Success",
      description: "Bulk request put on hold successfully!",
      variant: "default",
    });
    queryClient.refetchQueries({
      queryKey: ["cx", "disconnectionRequests", "summary"],
    });
    queryClient.refetchQueries({
      queryKey: ["cx", "disconnectionRequests", "list"],
    });
  };

  const handleExport = () => {
    downloadConsumers();
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await rejectRequest({ rejection_reason: reason });
      setCurrentStatus("rejected");
      setRejectDialogOpen(false);
      toast({
        title: "Success",
        description: "Bulk request rejected successfully!",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: ["cx", "disconnectionRequests", "summary"],
      });
      queryClient.refetchQueries({
        queryKey: ["cx", "disconnectionRequests", "list"],
      });
    } catch (error) {
      // Error handled by hook
    } finally {
      setIsRejecting(false);
    }
  };

  const handleResume = async () => {
    await resumeRequest();
    setCurrentStatus("in progress");
    toast({
      title: "Success",
      description: "Bulk request resumed successfully!",
      variant: "default",
    });
  };

  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        status?.toLowerCase() === "pending" ||
        status?.toLowerCase() === "pending approval" ||
        requestDetails?.statusDisplay === "Pending Approval" ||
        status?.toLowerCase() ===
          SERVICE_REQUEST_STATUS_DISPLAY[
            SERVICE_REQUEST_STATUS.PENDING
          ].toLowerCase(),
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        status?.toLowerCase() === "acknowledged" ||
        requestDetails?.statusDisplay === "Acknowledged",
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) =>
        status?.toLowerCase() === "hold" ||
        requestDetails?.statusDisplay === "Hold",
    },
  ];

  const request: any = {
    id: id || "1",
    requestNo: requestDetails?.batchNo,
    name: "Bulk Disconnection Request",
    type: "Bulk-Disconnection",
    status: currentStatus,
    dateRequested: "14 Apr 2026",
    priorityDisplay: "High",
    customerName: "Bulk",
    accountNumber: "N/A",
    category: "Disconnection",
    subcategory: "Bulk-Disconnection",
    priority: 1,
    reason: requestDetails?.requestType,

    // Bulk Specific Details
    batchId: "BATCH-47821",
    preferredDate: requestDetails?.preferredDate,
    timeSlot: requestDetails?.preferredTimeSlotDisplay,
    notes: requestDetails?.additionalNotes,

    // Audit info
    auditInfo: {
      createdBy: requestDetails?.audit?.createdBy,
      createdOn: requestDetails?.audit?.createdOn,
      approvedBy: requestDetails?.audit?.approvedBy,
      approvedOn: requestDetails?.audit?.approvedOn,
    },

    // Stats for the header
    stats: {
      total: requestDetails?.kpis?.totalConsumers || 0,
      completed: requestDetails?.kpis?.completedConsumers || 0,
      pending: requestDetails?.kpis?.pendingConsumers || 0,
      createdOn: requestDetails?.kpis?.createdOn,
    },
    progress: 33,
  };

  const urlMapping = {
    consumers: "consumers",
    meterReadings: "meterReadings",
    billGeneration: "billGeneration",
    notes: "notes",
    timeLine: "timeLine",
  };

  const tabComponents = {
    consumers: {
      label: "Consumers",
      component: <ConsumersBulkDisconnect />,
      count: request.stats.total,
    },
    meterReadings: {
      label: "Meter Readings",
      component: <MeterReadingsBulkDisconnect />,
    },
    billGeneration: {
      label: "Bill Generation",
      component: (
        <BillGenerationBulkDisconnect
          currentStatus={requestDetails?.statusDisplay}
          requestDetails={requestDetails}
        />
      ),
    },
    notes: {
      label: "Notes",
      component: (
        <NotesTab
          source={"bulk-disconnect"}
          requestId={request?.id.toString()}
          remoteUtilityId={Number(remoteUtilityId)}
          createdBy={request?.auditInfo?.createdBy}
        />
      ),
    },
    timeLine: {
      label: "Time Line",
      component: (
        <TimelineTab
          remoteUtilityId={Number(remoteUtilityId)}
          entityId={request?.id.toString()}
          actionType="bulk_request"
        />
      ),
    },
  };

  return (
    <div className="space-y-6">
      {/* Custom Header with Back Button and Actions */}
      <div className="flex items-center justify-between">
        <div className="mb-4 flex items-center gap-4">
          {/* Back Arrow */}
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/cx/disconnections")}
          />

          {/* Page Header */}
          <div className="flex flex-col">
            <PageHeader
              title="View Bulk Disconnection"
              subtitle="Track performance and progress of your bulk disconnection"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {actionButtons
            .filter((btn) => !btn.condition || btn.condition(request.status))
            .map((btn) => (
              <Button
                key={btn.id}
                className={`${btn.className} px-6 font-semibold`}
                variant={btn.variant}
                onClick={btn.onClick}
              >
                {btn.icon && <btn.icon className="h-4 w-4 mr-2" />}
                {btn.label}
              </Button>
            ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6 items-start">
        {/* Left Column: Details & Tabs */}
        <div className="col-span-9 flex flex-col gap-6">
          <BulkDisconnectDetailCard
            request={request}
            currentStatus={requestDetails?.statusDisplay}
          />

          <Tabs
            defaultValue="consumers"
            className="w-full"
            tabsListClassName="grid grid-cols-5 w-full bg-gradient-to-r from-blue-100/50 to-indigo-100/50 shadow-sm"
            urlMapping={urlMapping}
            tabComponents={tabComponents}
            level={0} // Top-level tabs
          />
        </div>

        {/* Right Column: Sidebar */}
        <div className="col-span-3 flex flex-col gap-4">
          <QuickActions
            consumerCount={request.stats.total}
            onNotify={() => setNotifyDialogOpen(true)}
            onExport={handleExport}
            onReject={() => setRejectDialogOpen(true)}
            onDownloadBills={downloadBills}
            isBillsGenerated={false}
            currentStatus={request?.status}
            isRejecting={isRejecting}
            isExporting={isExporting}
            isDownloadingBills={isDownloadingBills}
          />
          <TimelineTab
            remoteUtilityId={Number(remoteUtilityId)}
            entityId={request?.id.toString()}
            actionType="bulk_request"
            limit={TIMELINE_LIMIT}
          />
        </div>
      </div>

      {/* Modals */}
      <CommunicationDialog
        isOpen={isNotifyDialogOpen}
        onClose={() => setNotifyDialogOpen(false)}
        consumerId={request.id}
        email={emails}
        phone={phones}
        remoteUtilityId={Number(remoteUtilityId)}
        communicationType="bulk_disconnection"
        consumerCount={request.stats.total}
        useChannelType={useChannelType}
        useChannelTemplates={useChannelTemplates}
        useSendCommunication={createCommunicationWrapper}
      />

      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject Bulk Request"
        description="Please provide a reason for rejecting this bulk disconnection request."
        label="Rejection Reason"
        placeholder="Please provide a reason..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
};

export default BulkDisconnectLanding;
